import { Fragment, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { MapPin } from "lucide-react";
import UserMap from "./usermap";
import { useDispatch } from 'react-redux';
import { urls } from "../../services/apiService";

export default function AuthenticatedUsers({
  activeTab,
  setActiveTab,
  users,
  telusers,
  searchQuery,
  setSearchQuery,
  openModal,
  setIsDialogOpen,
  setSelectedUser,
  handlePasswordChange,
  isModalOpen,
  closeModal,
  selectedUser,
  newPlan,
  setNewPlan,
  loading,
  handleSavePlan,
  showMap,
  setShowMap
}) {
  const dispatch = useDispatch();

  // Fetch locations when component mounts
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await fetch(urls.map_data);
        const json = await res.json();

        const processed = json.data.map((item) => ({
          fieldName: item.fieldName,
          username: item.username,
          plan: item.plan,
          source: item.source,
          area_acres: item.area_acres,
          firstPoint: item.polygon?.length
            ? { lat: item.polygon[0].lat, lng: item.polygon[0].lng }
            : null,
        }));

        // Dispatch success action
        dispatch({
          type: "FETCH_USAGE_LOCATIONS_SUCCESS",
          payload: processed,
        });
      } catch (err) {
        // Dispatch error action
        dispatch({
          type: "FETCH_USAGE_LOCATIONS_FAILURE",
          payload: err.message,
        });
      }
    };

    fetchLocations();
  }, [dispatch]);

  // Filter users based on search query with null checks
  const filteredUsers = users.filter(user =>
    user?.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTelUsers = telusers.filter(user => {
    const msisdn = user?.msisdn || '';
    const fieldname = user?.fieldname || '';
    const query = searchQuery.toLowerCase();
    
    return msisdn.toLowerCase().includes(query) || 
           fieldname.toLowerCase().includes(query);
  });

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab("app")}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              activeTab === "app"
                ? "bg-gray-800 text-white shadow"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            App Users
          </button>

          <button
            onClick={() => setActiveTab("telco")}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              activeTab === "telco"
                ? "bg-gray-800 text-white shadow"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            Telenor Users
          </button>
        </div>
        
        {/* View on Map Button */}
        <button
          onClick={() => setShowMap(!showMap)}
          className={`px-6 py-2 rounded-lg font-medium transition flex items-center space-x-2 ${
            showMap
              ? "bg-red-600 text-white hover:bg-red-700"
              : "bg-green-600 text-white hover:bg-green-700"
          }`}
        >
          <MapPin className="w-4 h-4" />
          <span>{showMap ? "View Table" : "View on Map"}</span>
        </button>
      </div>

      {showMap ? (
        // Show Map - Pass activeTab to filter data by source
        <UserMap activeTab={activeTab} />
      ) : (
        // Show Table
        <>
          {activeTab === "app" && (
            <div className="bg-white text-gray-800 p-6 rounded-xl shadow-xl">
              <h2 className="text-xl font-semibold mb-6 text-gray-700">Authenticated Users</h2>
              {/* Search Input */}
              <input
                type="text"
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600"
              />
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-800 text-gray-100">
                    <th className="p-4 text-left rounded-tl-xl">ID</th>
                    <th className="p-4 text-left">User</th>
                    <th className="p-4 text-left">Plan</th>
                    <th className="p-4 text-left">Actions</th>
                    <th className="p-4 text-left">Device Management</th>
                    <th className="text-left rounded-tr-xl">Password Change</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user, index) => (
                      <tr
                        key={index}
                        className={`border-b border-gray-100 ${
                          index === filteredUsers.length - 1 ? "" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="p-4 font-medium">{index + 1}</td>
                        <td className="p-4">{user?.username || 'N/A'}</td>
                        <td className="p-4">
                          <span
                            className={`px-3 py-1 rounded-full text-sm ${
                              user?.plan === "Free"
                                ? "bg-gray-100 text-gray-600"
                                : user?.plan === "Diamond"
                                ? "bg-blue-100 text-blue-600"
                                : user?.plan === "Platinum"
                                ? "bg-purple-100 text-purple-600"
                                : "bg-purple-100 text-red-600"
                            }`}
                          >
                            {user?.plan || "Free"}
                          </span>
                        </td>
                        <td className="p-4 space-x-2">
                          <button
                            onClick={() => user && openModal(user)}
                            className="px-4 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
                            disabled={!user}
                          >
                            Update
                          </button>
                        </td>
                        <td className="p-4 space-x-2">
                          <button
                            onClick={() => {
                              if (user) {
                                setSelectedUser(user);
                                setIsDialogOpen(true);
                              }
                            }}
                            className="px-4 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
                            disabled={!user}
                          >
                            Device Info
                          </button>
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => user && handlePasswordChange(user)}
                            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors"
                            disabled={!user}
                          >
                            Change
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-gray-500">
                        {searchQuery ? "No users match your search" : "No users found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {activeTab === "telco" && (
            <div className="bg-white text-gray-800 p-6 rounded-xl shadow-xl">
              <h2 className="text-xl font-semibold mb-6 text-gray-700">Telenor Users</h2>
              <input
                type="text"
                placeholder="Search by MSISDN or Field Name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600"
              />
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-800 text-gray-100">
                    <th className="p-4 text-left rounded-tl-xl">ID</th>
                    <th className="p-4 text-left">MSISDN</th>
                    <th className="p-4 text-left">Field Name</th>
                    <th className="p-4 text-left rounded-tr-xl">Plan</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTelUsers.length > 0 ? (
                    filteredTelUsers.map((user, index) => (
                      <tr
                        key={index}
                        className={`border-b border-gray-100 ${
                          index === filteredTelUsers.length - 1 ? "" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="p-4 font-medium">{index + 1}</td>
                        <td className="p-4">{user?.msisdn || 'N/A'}</td>
                        <td className="p-4">{user?.fieldname || 'N/A'}</td>
                        <td className="p-4">
                          <span
                            className={`px-3 py-1 rounded-full text-sm ${
                              user?.plan === "Free"
                                ? "bg-gray-100 text-gray-600"
                                : user?.plan === "Diamond"
                                ? "bg-blue-100 text-blue-600"
                                : user?.plan === "Platinum"
                                ? "bg-purple-100 text-purple-600"
                                : "bg-purple-100 text-red-600"
                            }`}
                          >
                            {user?.plan || "Free"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="p-8 text-center text-gray-500">
                        {searchQuery ? "No Telenor users match your search" : "No Telenor users found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      
      {/* Plan Update Modal */}
      <Dialog open={isModalOpen} onClose={closeModal} className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30">
        <Dialog.Panel className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full mx-4">
          <Dialog.Title className="text-2xl font-bold mb-6 text-gray-800">
            Update Plan for {selectedUser?.username || 'User'}
          </Dialog.Title>
          <select
            value={newPlan}
            onChange={(e) => setNewPlan(e.target.value)}
            className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="Free">Free</option>
            <option value="Diamond">Diamond</option>
            <option value="Platinum">Platinum</option>
          </select>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={closeModal}
              className="px-5 py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSavePlan}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              {loading ? "Saving Changes..." : "Save Changes"}
            </button>
          </div>
        </Dialog.Panel>
      </Dialog>
    </>
  );
}